import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function Home({ session, onCreateMatch, onSchedule, onOpenLobby, onOpenTable }) {
  const [activeBets, setActiveBets] = useState([])
  const [activeOneVOnes, setActiveOneVOnes] = useState([])
  const [oneVOneProfiles, setOneVOneProfiles] = useState({})
  const [activeTables, setActiveTables] = useState([])
  const [collapsed, setCollapsed] = useState({})
  const [scheduledMatches, setScheduledMatches] = useState([])
  const [profiles, setProfiles] = useState({})
  const [loading, setLoading] = useState(true)
  const [pendingHome, setPendingHome] = useState({})

  const doubleTap = (key, onConfirm) => {
    if (pendingHome[key]) {
      setPendingHome(prev => ({ ...prev, [key]: false }))
      onConfirm()
    } else {
      setPendingHome(prev => ({ ...prev, [key]: true }))
      setTimeout(() => setPendingHome(prev => ({ ...prev, [key]: false })), 3000)
    }
  }

  useEffect(() => {
    const load = async () => {
      // All my matches (created + joined)
      const { data: created } = await supabase.from('matches').select('*').eq('creator_id', session.user.id).neq('state', 'archived')
      const { data: joined } = await supabase.from('match_players').select('match_id').eq('player_id', session.user.id).eq('status', 'accepted')
      let joinedMatches = []
      if (joined && joined.length > 0) {
        const ids = joined.map(j => j.match_id)
        const { data: jm } = await supabase.from('matches').select('*').in('id', ids).neq('state', 'archived')
        joinedMatches = jm || []
      }
      const allMatches = [...(created || []), ...joinedMatches.filter(m => !(created || []).find(c => c.id === m.id))]

      // Scheduled — golf matches with tee time
      const matchScheduled = allMatches
        .filter(m => m.tee_time)
        .map(m => ({ ...m, _type: 'match', _date: m.tee_time }))

      // Scheduled — 1v1 bets with event date
      const { data: ovoSched } = await supabase.from('one_v_one').select('*')
        .or(`created_by.eq.${session.user.id},opponent_id.eq.${session.user.id}`)
        .in('status', ['active', 'claimed', 'disputed'])
        .not('settle_by', 'is', null)
      const ovoScheduled = (ovoSched || []).map(b => ({ ...b, _type: '1v1', _date: b.settle_by }))

      // Scheduled — tables with scheduled_at
      const { data: myTablesSched } = await supabase.from('crossbet_table_players').select('table_id').eq('player_id', session.user.id).eq('status', 'accepted')
      let tableScheduled = []
      if (myTablesSched && myTablesSched.length > 0) {
        const tids = myTablesSched.map(t => t.table_id)
        const { data: tables } = await supabase.from('crossbet_tables').select('*').in('id', tids).in('status', ['pending', 'active']).not('scheduled_at', 'is', null)
        tableScheduled = (tables || []).map(t => ({ ...t, _type: 'table', _date: t.scheduled_at }))
      }

      const now = new Date()
      const scheduled = [...matchScheduled, ...ovoScheduled, ...tableScheduled]
        .filter(i => new Date(i._date) >= now)
        .sort((a, b) => new Date(a._date) - new Date(b._date))
        .slice(0, 5)
      setScheduledMatches(scheduled)

      // Active betting lines
      const matchIds = allMatches.map(m => m.id)
      if (matchIds.length > 0) {
        const { data: lines } = await supabase
          .from('betting_lines')
          .select('*')
          .in('match_id', matchIds)
          .eq('status', 'active')
          .contains('player_ids', [session.user.id])

        // Group by match
        const grouped = {}
        for (const line of lines || []) {
          if (!grouped[line.match_id]) grouped[line.match_id] = { match: allMatches.find(m => m.id === line.match_id), lines: [] }
          grouped[line.match_id].lines.push(line)
        }
        setActiveBets(Object.values(grouped))

        // Load profiles for all players in active lines
        const playerIds = [...new Set((lines || []).flatMap(l => l.player_ids))]
        if (playerIds.length > 0) {
          const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name').in('id', playerIds)
          const pmap = {}
          prof?.forEach(p => { pmap[p.id] = p })
          setProfiles(pmap)
        }
      }

      // Active Tables
      const { data: myTablePlayers } = await supabase
        .from('crossbet_table_players')
        .select('table_id')
        .eq('player_id', session.user.id)
        .eq('status', 'accepted')
      if (myTablePlayers && myTablePlayers.length > 0) {
        const tids = myTablePlayers.map(t => t.table_id)
        const { data: tables } = await supabase
          .from('crossbet_tables')
          .select('*')
          .in('id', tids)
          .in('status', ['pending', 'active'])
        setActiveTables(tables || [])
      }

      // Active 1v1s
      const { data: ovo } = await supabase
        .from('one_v_one')
        .select('*')
        .or(`created_by.eq.${session.user.id},opponent_id.eq.${session.user.id}`)
        .in('status', ['pending', 'active', 'claimed', 'disputed'])
      setActiveOneVOnes(ovo || [])
      if (ovo && ovo.length > 0) {
        const ids = [...new Set(ovo.flatMap(b => [b.created_by, b.opponent_id]))]
        const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name').in('id', ids)
        const pmap = {}
        prof?.forEach(p => { pmap[p.id] = p })
        setOneVOneProfiles(pmap)
      }

      setLoading(false)
    }
    load()

    // Real-time updates for 1v1 bets
    const sub = supabase
      .channel('home_1v1')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'one_v_one',
        filter: `created_by=eq.${session.user.id}`
      }, () => load())
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'one_v_one',
        filter: `opponent_id=eq.${session.user.id}`
      }, () => load())
      .subscribe()

    return () => supabase.removeChannel(sub)
  }, [session])

  const fmt = (dt) => dt ? new Date(dt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

  const FORMAT_LABEL = { stroke_play: 'STROKE PLAY', skins: 'SKINS', nassau: 'NASSAU' }
  const FORMAT_ICON = { stroke_play: '🏌️', skins: '🎯', nassau: '3️⃣' }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#5A5550', fontFamily: 'DM Sans, sans-serif' }}>Loading...</div>
    </div>
  )

  return (
    <div style={s.screen}>

      <div style={s.body}>

        {/* Wallet Card */}
        <div style={s.walletCard}>
          <div style={s.walletRow}>
            <div>
              <div style={s.walletLabel}>AVAILABLE BALANCE</div>
              <div style={s.walletAmount}>$0<span style={s.walletCents}>.00</span></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={s.walletLabel}>IN ESCROW</div>
              <div style={s.escrowAmount}>$0</div>
            </div>
          </div>
          <div style={s.walletBtns}>
            <button style={s.depositBtn}>+ Deposit</button>
            <button style={s.withdrawBtn}>Cash Out</button>
          </div>
        </div>

        {/* Active Crossbets */}
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle}>ACTIVITY</div>
        </div>

        {activeBets.length === 0 && activeOneVOnes.length === 0 && activeTables.length === 0 ? (
          <div style={s.emptyCard}>
            <img src='/crossbet-logo.png' alt='Crossbet' style={{ width: 72, height: 72, objectFit: 'contain', marginBottom: 8, opacity: 0.7 }} />
            <div style={s.emptyText}>No active bets</div>
            <div style={s.emptySub}>Create a match, 1v1 or table to get started</div>
            <button style={s.createBtn} onClick={onCreateMatch}>New CROSSBET →</button>
          </div>
        ) : (
          <div style={s.betsContainer}>
            {activeBets.map(group => (
              <div key={group.match?.id} style={s.betGroupNew}>
                <div style={s.betGroupHeaderNew} onClick={() => setCollapsed(p => ({ ...p, [group.match?.id]: !p[group.match?.id] }))}>
                  <div style={s.betGroupNameNew}>{group.match?.name || group.match?.course_name || 'Match'}</div>
                  {group.match?.tee_time && <div style={s.betGroupTimeNew}>{fmt(group.match.tee_time)}</div>}
                  <span style={s.betGroupNav} onClick={e => { e.stopPropagation(); onOpenLobby(group.match?.id) }}>→</span>
                  <div style={s.betGroupChevron}>{collapsed[group.match?.id] ? '▶' : '▼'}</div>
                </div>
                {group.lines.map(line => (
                  <div key={line.id} style={s.betLineNew}>
                    <span style={s.betLineIconNew}>{FORMAT_ICON[line.format]}</span>
                    <div style={s.betLineInfoNew}>
                      <div style={s.betLineLabelNew}>{FORMAT_LABEL[line.format]}</div>
                      <div style={s.betLinePlayersNew}>{line.player_ids.map(pid => profiles[pid]?.first_name || '?').join(' vs ')}</div>
                    </div>
                    <div style={s.betLineAmtNew}>${line.wager_amount}</div>
                  </div>
                ))}
              </div>
            ))}
            {activeOneVOnes.map(bet => {
              const other = bet.created_by === session.user.id ? bet.opponent_id : bet.created_by
              const otherProf = oneVOneProfiles[other]
              const isCreator = bet.created_by === session.user.id
              const needsAction = bet.status === 'active' || bet.status === 'disputed' || (bet.status === 'claimed' && bet.action_by !== session.user.id) || (bet.status === 'pending' && !isCreator)
              return (
                <div key={bet.id} style={{ ...s.bet1v1Row, flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={s.bet1v1Left}>
                      <div style={s.bet1v1Title}>vs {otherProf ? `${otherProf.first_name} ${otherProf.last_name}` : '?'}</div>
                      <div style={s.bet1v1Desc}>{bet.description}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <div style={s.betLineAmtNew}>${bet.wager_amount}</div>
                      {bet.status === 'claimed' && bet.action_by === session.user.id && (
                        <div style={s.waitingClaim}>⏳ Pending</div>
                      )}
                      {bet.status === 'pending' && isCreator && (
                        <button style={{ background: 'transparent', border: '1.5px solid #E0DDD4', borderRadius: 8, padding: '6px 12px', fontSize: 11, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' }}
                          onClick={() => doubleTap(bet.id+'_withdraw', async () => {
                            await supabase.from('one_v_one').update({ status: 'withdrawn' }).eq('id', bet.id)
                            setActiveOneVOnes(prev => prev.filter(b => b.id !== bet.id))
                          })}>
                          {pendingHome[bet.id+'_withdraw'] ? 'Confirm?' : 'Withdraw'}
                        </button>
                      )}
                      {bet.status === 'active' && (() => {
                        const deadline = bet.settle_by ? new Date(bet.settle_by).getTime() + 72*3600000 : bet.activated_at ? new Date(bet.activated_at).getTime() + 72*3600000 : null
                        if (!deadline) return null
                        const msLeft = deadline - Date.now()
                        const hrsLeft = Math.max(0, Math.floor(msLeft / 3600000))
                        if (hrsLeft >= 24) return null
                        return <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: '#C19A6B', fontWeight: 700 }}>⏰ {hrsLeft}h to settle</div>
                        </div>
                      })()}
                      {bet.status === 'disputed' && (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: '#C0392B', fontWeight: 700 }}>DISPUTED</div>
                          <div style={{ fontSize: 11, color: '#9B7D52', fontWeight: 500 }}>
                            {bet.disputed_at ? (() => {
                              const hrs = Math.max(0, 24 - Math.floor((Date.now() - new Date(bet.disputed_at)) / 3600000))
                              return `${hrs}h left`
                            })() : '24h left'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {needsAction && (
                    <div style={{ borderTop: '1px solid #F0EDE4', marginTop: 10, paddingTop: 10 }}>
                      {bet.status === 'claimed' && bet.action_by !== session.user.id && (
                        <div style={{ fontSize: 12, color: '#9B7D52', fontWeight: 600, marginBottom: 8 }}>{oneVOneProfiles[bet.action_by]?.first_name} claims {bet.action_by === bet.claimed_by ? 'a win' : 'a loss'} — do you agree?</div>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        {bet.status === 'pending' && !isCreator ? (
                          <>
                            <div style={{ fontSize: 11, color: '#9B7D52', fontWeight: 600, marginBottom: 8 }}>⚔️ {otherProf?.first_name} challenges you</div>
                            <button style={pendingHome[bet.id+'_accept'] ? { ...s.actionBtn, background: '#1A5C3A' } : { ...s.actionBtn, background: '#00563F' }}
                              onClick={() => doubleTap(bet.id+'_accept', async () => {
                                const now = new Date().toISOString()
                                await supabase.from('one_v_one').update({ status: 'active', activated_at: now }).eq('id', bet.id)
                                setActiveOneVOnes(prev => prev.map(b => b.id === bet.id ? { ...b, status: 'active', activated_at: now } : b))
                              })}>
                              {pendingHome[bet.id+'_accept'] ? 'Confirm?' : '✓ Accept'}
                            </button>
                            <button style={pendingHome[bet.id+'_decline'] ? { ...s.actionBtn, background: '#C0392B' } : { ...s.actionBtn, background: '#E8821A' }}
                              onClick={() => doubleTap(bet.id+'_decline', async () => {
                                await supabase.from('one_v_one').update({ status: 'cancelled' }).eq('id', bet.id)
                                setActiveOneVOnes(prev => prev.filter(b => b.id !== bet.id))
                              })}>
                              {pendingHome[bet.id+'_decline'] ? 'Confirm?' : 'Decline'}
                            </button>
                          </>
                        ) : bet.status === 'claimed' && bet.action_by !== session.user.id ? (
                          <>
                            <button style={pendingHome[bet.id+'_agree'] ? { ...s.actionBtn, background: '#1A5C3A' } : { ...s.actionBtn, background: '#00563F' }}
                              onClick={() => doubleTap(bet.id+'_agree', async () => {
                                await supabase.from('one_v_one').update({ status: 'settled', confirmed_by: session.user.id }).eq('id', bet.id)
                                setActiveOneVOnes(prev => prev.filter(b => b.id !== bet.id))
                              })}>
                              {pendingHome[bet.id+'_agree'] ? 'Confirm?' : '✓ Agree'}
                            </button>
                            <button style={pendingHome[bet.id+'_disagree'] ? { ...s.actionBtn, background: '#C0392B' } : { ...s.actionBtn, background: '#E8821A' }}
                              onClick={() => doubleTap(bet.id+'_disagree', async () => {
                                const updateData = { status: 'disputed', claimed_by: null, claimed_against: null }
                                if (!bet.disputed_at) updateData.disputed_at = new Date().toISOString()
                                await supabase.from('one_v_one').update(updateData).eq('id', bet.id)
                                setActiveOneVOnes(prev => prev.map(b => b.id === bet.id ? { ...b, status: 'disputed', claimed_by: null, claimed_against: null, disputed_at: b.disputed_at || new Date().toISOString() } : b))
                              })}>
                              {pendingHome[bet.id+'_disagree'] ? 'Confirm?' : 'Disagree'}
                            </button>
                          </>
                        ) : (
                          <>
                            <button style={pendingHome[bet.id+'_won'] ? { ...s.actionBtn, background: '#1A5C3A' } : { ...s.actionBtn, background: '#00563F' }}
                              onClick={() => doubleTap(bet.id+'_won', async () => {
                                const otherPlayer = bet.created_by === session.user.id ? bet.opponent_id : bet.created_by
                                await supabase.from('one_v_one').update({ status: 'claimed', claimed_by: session.user.id, claimed_against: otherPlayer, action_by: session.user.id }).eq('id', bet.id)
                                setActiveOneVOnes(prev => prev.map(b => b.id === bet.id ? { ...b, status: 'claimed', claimed_by: session.user.id, claimed_against: otherPlayer, action_by: session.user.id } : b))
                              })}>
                              {pendingHome[bet.id+'_won'] ? 'Confirm?' : 'I Won'}
                            </button>
                            <button style={pendingHome[bet.id+'_lost'] ? { ...s.actionBtn, background: '#C0392B' } : { ...s.actionBtn, background: '#E8821A' }}
                              onClick={() => doubleTap(bet.id+'_lost', async () => {
                                const otherPlayer = bet.created_by === session.user.id ? bet.opponent_id : bet.created_by
                                await supabase.from('one_v_one').update({ status: 'claimed', claimed_by: otherPlayer, claimed_against: session.user.id, action_by: session.user.id }).eq('id', bet.id)
                                setActiveOneVOnes(prev => prev.map(b => b.id === bet.id ? { ...b, status: 'claimed', claimed_by: otherPlayer, claimed_against: session.user.id, action_by: session.user.id } : b))
                              })}>
                              {pendingHome[bet.id+'_lost'] ? 'Confirm?' : 'I Lost'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
            {activeTables.map(t => (
              <div key={t.id} style={s.betGroupNew}>
                <div style={s.betGroupHeaderNew} onClick={() => onOpenTable && onOpenTable(t.id)}>
                  <div style={s.betGroupNameNew}>🎯 {t.name}</div>
                  <div style={s.betGroupTimeNew}>{t.status.toUpperCase()}</div>
                  <div style={s.betGroupChevron}>→</div>
                </div>
                <div style={s.betLineNew}>
                  <div style={s.betLineInfoNew}>
                    <div style={s.betLineLabelNew}>{t.payout_format === 'winner_takes_all' ? 'Winner Takes All' : 'Custom Split'}{t.lives_enabled ? ` · ${t.lives_per_player} lives` : ''}</div>
                  </div>
                  <div style={s.betLineAmtNew}>${t.wager_amount}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Scheduled Matches */}
        <div style={{ ...s.sectionHeader, marginTop: 8 }}>
          <div style={s.sectionTitle}>SCHEDULED</div>
          <span style={s.viewAll} onClick={onSchedule}>View All →</span>
        </div>

        {scheduledMatches.length === 0 ? (
          <div style={s.emptyCard}>
            <div style={s.emptyIcon}>◷</div>
            <div style={s.emptyText}>Nothing scheduled</div>
            <div style={s.emptySub}>Matches with a date and time will appear here</div>
          </div>
        ) : scheduledMatches.map(m => (
          <div key={m.id} style={s.matchCard} onClick={() => {
            if (m._type === 'match') onOpenLobby(m.id)
            else if (m._type === 'table') onOpenTable && onOpenTable(m.id)
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={s.matchName}>
                {m._type === 'match' ? (m.name || m.course_name || 'Match') :
                 m._type === '1v1' ? (() => { const otherId = m.created_by === session.user.id ? m.opponent_id : m.created_by; const p = oneVOneProfiles[otherId]; return `⚔️ vs ${p ? p.first_name + ' ' + p.last_name : '?'}` })() :
                 `🎯 ${m.name}`}
              </div>
              {m._type === '1v1' && <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: '#C19A6B' }}>${m.wager_amount}</div>}
            </div>
            <div style={s.matchMeta}>{fmt(m._date)}</div>
            {m._type === 'match' && <div style={s.matchCourse}>{m.course_name} · {m.num_holes} holes · Par {m.course_par}</div>}
            {m._type === '1v1' && <div style={s.matchCourse}>{m.description}</div>}
          </div>
        ))}

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  name: { fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 34, color: '#FDF9E7', letterSpacing: 2 },
  body: { padding: '20px 24px 32px', display: 'flex', flexDirection: 'column', gap: 10 },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 18, color: '#1A1814', letterSpacing: 1 },
  viewAll: { fontSize: 13, color: '#00563F', fontWeight: 600, cursor: 'pointer' },
  emptyCard: { background: '#FFFFFF', borderRadius: 20, padding: 28, border: '1px solid #E0DDD4', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyIcon: { fontSize: 36, marginBottom: 10, color: '#C19A6B' },
  emptyText: { fontSize: 17, fontWeight: 600, color: '#1A1814', marginBottom: 4 },
  emptySub: { fontSize: 14, color: '#5A5550', marginBottom: 16 },
  createBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  betsContainer: { background: '#FDF9E7', borderRadius: 20, border: '1px solid #E0DDD4', padding: 10, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto' },
  betGroupNew: { background: '#FFFFFF', borderRadius: 16, border: '1px solid #E0DDD4', overflow: 'hidden' },
  betGroupHeaderNew: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#0A0A0A', cursor: 'pointer' },
  betGroupNameNew: { flex: 1, fontSize: 13, fontWeight: 700, color: '#FDF9E7' },
  betGroupTimeNew: { fontSize: 11, color: '#555' },
  betGroupNav: { fontSize: 14, color: '#C19A6B', cursor: 'pointer', padding: '0 4px' },
  betGroupChevron: { fontSize: 10, color: '#555' },
  betLineNew: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderTop: '1px solid #F8F6F2' },
  betLineIconNew: { fontSize: 14, flexShrink: 0 },
  betLineInfoNew: { flex: 1 },
  betLineLabelNew: { fontSize: 12, fontWeight: 700, color: '#1A1814' },
  betLinePlayersNew: { fontSize: 11, color: '#5A5550' },
  betLineAmtNew: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: '#C19A6B', flexShrink: 0, marginRight: 8 },
  bet1v1Row: { background: '#FFFFFF', borderRadius: 16, border: '1px solid #E0DDD4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' },
  bet1v1Left: { flex: 1 },
  bet1v1Title: { fontSize: 15, fontWeight: 700, color: '#1A1814' },
  bet1v1Desc: { fontSize: 13, color: '#5A5550', marginTop: 4 },
  waitingClaim: { fontSize: 11, color: '#C19A6B', fontWeight: 700 },
  actionBtn: { flex: 1, background: '#00563F', color: 'white', border: 'none', borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  walletCard: { background: '#0A0A0A', borderRadius: 20, padding: 20, border: '1px solid #1E1E1E' },
  walletRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 16 },
  walletLabel: { fontSize: 10, color: '#444', letterSpacing: 1.5, textTransform: 'uppercase' },
  walletAmount: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, color: '#FDF9E7', lineHeight: 1.1, marginTop: 4 },
  walletCents: { fontSize: 22, color: '#444' },
  escrowAmount: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: '#555', marginTop: 4 },
  walletBtns: { display: 'flex', gap: 10 },
  depositBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  withdrawBtn: { flex: 1, background: 'transparent', color: '#FDF9E7', border: '1.5px solid #333', borderRadius: 14, padding: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  matchCard: { background: '#FFFFFF', borderRadius: 16, padding: '14px 16px', border: '1px solid #E0DDD4', cursor: 'pointer' },
  matchName: { fontSize: 16, fontWeight: 700, color: '#1A1814', marginBottom: 4 },
  matchMeta: { fontSize: 14, color: '#00563F', fontWeight: 600, marginBottom: 2 },
  matchCourse: { fontSize: 12, color: '#5A5550' },
}
